import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaArrowRight, FaArrowUpFromBracket } from 'react-icons/fa6';
import React, { Fragment, useRef } from 'react';
import { MdOutlineUploadFile } from 'react-icons/md';
import { Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { FaExclamationTriangle } from 'react-icons/fa';
import { ExcelColumn } from '../../../../types/misc';
import IconBadge from '../../icons/IconBadge';
import { downloadExcelExample, parseColumns } from '../../../../utils/fileUtils';
import { CommunityInvite } from '../../../../models/inviteService.model';
import useWindowDimensions from '../../../../hooks/effects/useWindowDimensions';
import { getSelectedCommunity } from '../../../../hooks/community/useSelectedCommunity';
import PageHeader from '../../misc/PageHeader';

interface ImportViewHeaderProps {
  invite: CommunityInvite;
}

export function ImportViewHeader({ invite }: ImportViewHeaderProps): JSX.Element {
  const { t } = useTranslation();

  const downloadExampleFile = () => {
    downloadExcelExample(invite);
    toast.success(t('toast.success.downloadSuccess'));
  };

  return (
    <>
      <PageHeader
        title={t('component.modal.importLocalContacts.import.title')}
        subtitle={
          <Trans
            i18nKey="component.modal.importLocalContacts.import.subtitle"
            className="text-primary-900">
            Je kunt hier contacten importeren met Excel. Download
            <strong className="cursor-pointer font-semibold text-primary underline" onClick={downloadExampleFile}>
              hier
            </strong>
          </Trans>
        }
      />
    </>
  );
}

interface ImportViewProps {
  setColumns: (columns: ExcelColumn[] | undefined) => void;
  setFileName: (name: string) => void;
  invite: CommunityInvite;
}

export function ImportView({ setColumns, setFileName, invite }: ImportViewProps): JSX.Element {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const community = getSelectedCommunity();

  const uploadExcelFile = async (file: File) => {
    setFileName(file.name);
    const columns = await parseColumns(file).catch(() => {
      toast.error('Er ging iets fout bij het importeren van je bestand');
      return [];
    });

    setColumns(columns);
  };

  const handleFileUpload = (files: FileList) => {
    if (files.length === 1) {
      const file = files[0];
      uploadExcelFile(file);
    } else {
      toast.error(t('toast.error.oneFile'));
    }
  };

  if (community && !community.inviteFields) {
    return (
      <>
        <div className="mb-8">
          <ImportViewHeader invite={invite} />
        </div>
        <div className="mb-1 flex h-fit w-full flex-col items-center justify-center gap-4 rounded-2xl border-2 border-secondary-200 bg-secondary-50 p-8 shadow-sm">
          <div className="rounded-full bg-secondary-200 p-4">
            <FaExclamationTriangle size={32} className="text-primary" />
          </div>
          <span className="text-center text-lg font-medium text-primary-900">
            {t('component.modal.importLocalContacts.import.missingRequestData')}
          </span>
          <Link
            to="/settings/requested-data"
            className="flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-white transition-all">
            {t('component.modal.importLocalContacts.import.toRequestedData')}
            <FaArrowRight size={14} />
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-8">
        <ImportViewHeader invite={invite} />
      </div>
      <div
        onDrop={(e) => {
          e.preventDefault();
          handleFileUpload(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className="group mb-4 flex h-fit w-full cursor-pointer flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-primary-300 bg-secondary-50 p-12 transition-all">
        <div className="rounded-full bg-primary-300 p-6 transition-all">
          <MdOutlineUploadFile className="h-16 w-16 text-primary transition-all group-hover:text-primary-700" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <span className="text-center text-lg font-semibold text-primary-900">
            <Trans i18nKey="component.modal.importLocalContacts.import.importButton" />
          </span>
          <span className="text-sm text-gray-500">
            {t('component.modal.importLocalContacts.import.fileSupported')}
          </span>
        </div>
      </div>
      <input
        type="file"
        className="hidden"
        onChange={(e) => handleFileUpload(e.target.files!)}
        accept=".xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ref={inputRef}
      />
    </>
  );
}
